import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Briefcase, 
  FileText, 
  Users, 
  FolderKanban, 
  Ticket, 
  BarChart3,
  Settings,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  UserPlus,
  MessageSquare
} from 'lucide-react';
import { adminAPI } from '../../services/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    counts: { users: 0, clients: 0, blogs: 0, leads: 0, projects: 0, tickets: 0 },
    recentActivity: { newLeadsThisMonth: 0, newTicketsThisWeek: 0, activeChats: 0, escalatedChats: 0 },
    breakdowns: { leads: [], tickets: [], projects: [] }
  });
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await adminAPI.getDashboard();
      setStats(response.data.stats);
      setRecentLeads(response.data.recentLeads || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const sidebarLinks = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/admin', active: true },
    { name: 'Services', icon: Briefcase, path: '/admin/services' },
    { name: 'Blog Posts', icon: FileText, path: '/admin/blogs' },
    { name: 'Leads', icon: UserPlus, path: '/admin/leads' },
    { name: 'Projects', icon: FolderKanban, path: '/admin/projects' },
    { name: 'Tickets', icon: Ticket, path: '/admin/tickets' },
    { name: 'Users', icon: Users, path: '/admin/users' },
    { name: 'Analytics', icon: BarChart3, path: '/admin/analytics' },
  ];

  const statCards = [
    { 
      name: 'Total Users', 
      value: stats.counts.users, 
      change: '+12%', 
      trend: 'up',
      icon: Users, 
      color: 'bg-blue-500' 
    },
    { 
      name: 'New Leads', 
      value: stats.recentActivity.newLeadsThisMonth, 
      change: '+8%', 
      trend: 'up',
      icon: UserPlus, 
      color: 'bg-green-500' 
    },
    { 
      name: 'Open Tickets', 
      value: stats.counts.tickets, 
      change: '-5%', 
      trend: 'down',
      icon: Ticket, 
      color: 'bg-orange-500' 
    },
    { 
      name: 'Active Projects', 
      value: stats.counts.projects, 
      change: '+3%', 
      trend: 'up',
      icon: FolderKanban, 
      color: 'bg-purple-500' 
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-primary text-white min-h-screen fixed left-0 top-0 hidden lg:block">
          <div className="p-6">
            <Link to="/admin" className="flex items-center gap-2 mb-8">
              <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">TD</span>
              </div>
              <span className="font-bold text-lg">Admin Panel</span>
            </Link>
            
            <nav className="space-y-1">
              {sidebarLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    link.active 
                      ? 'bg-accent text-white' 
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-64">
          {/* Header */}
          <header className="bg-white border-b border-slate-200 px-8 py-4">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-primary">Dashboard Overview</h1>
              <div className="flex items-center gap-4">
                <Link to="/" className="text-slate-500 hover:text-primary">
                  View Site
                </Link>
              </div>
            </div>
          </header>

          <div className="p-8">
            {/* Stats Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {statCards.map((stat, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-slate-500 text-sm">{stat.name}</p>
                      <p className="text-3xl font-bold text-primary mt-1">{stat.value}</p>
                      <div className={`flex items-center gap-1 mt-2 text-sm ${
                        stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {stat.trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                        {stat.change} from last month
                      </div>
                    </div>
                    <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center text-white`}>
                      <stat.icon className="w-6 h-6" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {/* Recent Leads */}
              <div className="lg:col-span-2 bg-white rounded-xl shadow-sm">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="text-lg font-bold text-primary">Recent Leads</h2>
                  <Link to="/admin/leads" className="text-accent text-sm font-medium flex items-center gap-1">
                    View All
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className="p-6">
                  {recentLeads.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="table-custom">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Company</th>
                            <th>Status</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentLeads.map((lead) => (
                            <tr key={lead._id}>
                              <td>
                                <div>
                                  <p className="font-medium text-primary">{lead.name}</p>
                                  <p className="text-sm text-slate-500">{lead.email}</p>
                                </div>
                              </td>
                              <td>{lead.company || '-'}</td>
                              <td>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  lead.status === 'new' ? 'bg-blue-100 text-blue-700' :
                                  lead.status === 'contacted' ? 'bg-yellow-100 text-yellow-700' :
                                  lead.status === 'qualified' ? 'bg-green-100 text-green-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                  {lead.status}
                                </span>
                              </td>
                              <td className="text-slate-500">
                                {new Date(lead.createdAt).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      No recent leads
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions & Activity */}
              <div className="space-y-6">
                {/* Quick Actions */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-bold text-primary mb-4">Quick Actions</h2>
                  <div className="space-y-3">
                    {[
                      { name: 'Add New Blog Post', path: '/admin/blogs', icon: FileText },
                      { name: 'Manage Services', path: '/admin/services', icon: Briefcase },
                      { name: 'View Analytics', path: '/admin/analytics', icon: BarChart3 },
                      { name: 'Manage Users', path: '/admin/users', icon: Users },
                    ].map((action, index) => (
                      <Link
                        key={index}
                        to={action.path}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        <action.icon className="w-5 h-5 text-accent" />
                        <span className="text-slate-700">{action.name}</span>
                        <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Activity Summary */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-bold text-primary mb-4">Activity Summary</h2>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <MessageSquare className="w-5 h-5 text-blue-500" />
                        <span className="text-slate-600">Active Chats</span>
                      </div>
                      <span className="font-semibold text-primary">{stats.recentActivity.activeChats}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <MessageSquare className="w-5 h-5 text-orange-500" />
                        <span className="text-slate-600">Escalated Chats</span>
                      </div>
                      <span className="font-semibold text-primary">{stats.recentActivity.escalatedChats}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Ticket className="w-5 h-5 text-red-500" />
                        <span className="text-slate-600">New Tickets (Week)</span>
                      </div>
                      <span className="font-semibold text-primary">{stats.recentActivity.newTicketsThisWeek}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
